import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Checkbox, Typography, CircularProgress, Alert, Chip, Box
} from '@mui/material';
import { getMyTeams, shareWaxingEntry, unshareWaxingEntry, getSharedTeams } from '../api';

export default function ShareWaxingEntryModal({ open, onClose, entry }) {
  const [teams, setTeams] = useState([]);
  const [sharedTeams, setSharedTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!entry) return;
    
    try {
      setLoading(true);
      const [teamsData, sharedData] = await Promise.all([
        getMyTeams(),
        getSharedTeams(entry.id)
      ]);
      setTeams(teamsData);
      setSharedTeams(sharedData);
      setError(null);
    } catch (err) {
      setError('Failed to load teams');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [entry]);

  useEffect(() => {
    if (open && entry) {
      loadData();
    }
  }, [open, loadData, entry]);

  const handleShareToggle = async (teamId, isShared) => {
    if (!entry) return;
    
    try {
      setSharing(true);
      if (isShared) {
        await unshareWaxingEntry(entry.id, teamId);
        setSharedTeams(prev => prev.filter(t => t.id !== teamId));
      } else {
        await shareWaxingEntry(entry.id, teamId);
        const team = teams.find(t => t.id === teamId);
        setSharedTeams(prev => [...prev, { id: teamId, name: team.name, shared_at: new Date().toISOString() }]);
      }
    } catch (err) {
      setError('Failed to update sharing');
      console.error(err);
    } finally {
      setSharing(false);
    }
  };

  const isSharedWithTeam = (teamId) => {
    return sharedTeams.some(team => team.id === teamId);
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Share Waxing Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Share Waxing Entry: {entry?.title}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select teams to share this waxing entry with. Other coaches in these teams will be able to see this entry.
        </Typography>

        <List>
          {teams.map((team) => {
            const isShared = isSharedWithTeam(team.id);
            return (
              <ListItem key={team.id} dense>
                <Checkbox
                  edge="start"
                  checked={isShared}
                  onChange={() => handleShareToggle(team.id, isShared)}
                  disabled={sharing}
                />
                <ListItemText
                  primary={team.name}
                  secondary={team.description || 'No description'}
                />
                <ListItemSecondaryAction>
                  {isShared && (
                    <Chip 
                      label="Shared" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>

        {teams.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            You are not a member of any teams yet.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
} 